/**
 * KUKA component tree for an asset: robots/KRCs (slot 1/2) with the parts
 * inside them, system-level parts (e.g. Beckhoff PLC), movement action and
 * per-part history. Part categories come from the configurable
 * component_types list (Settings -> Equipment), so new kinds appear here
 * without code changes. Every move writes the component_movements ledger
 * server-side.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/format';
import './AssetComponentsPanel.css';

// Fallback labels if the types endpoint is unreachable — core set only.
const FALLBACK_LABELS = {
  robot: 'Robot',
  krc: 'KRC Controller',
  kpp: 'KPP',
  ksp: 'KSP',
  pc: 'PC',
  robot_wrist: 'Robot Wrist',
  resolver: 'Resolver',
  adapter: 'Adapter 2',
  smartpad: 'SmartPAD'
};

const OFF_STATUSES = [
  { value: 'workshop', label: 'Workshop' },
  { value: 'service_pool', label: 'Service pool' },
  { value: 'out_for_repair', label: 'Out for repair (sent away)' },
  { value: 'scrapped', label: 'Scrapped' }
];

const READ_ONLY_ROLES = ['commercial', 'operations_coordinator'];

export function useComponentTypes() {
  const { authFetch } = useAuth();
  const [types, setTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/v1/components/types');
        if (response.ok && !cancelled) setTypes((await response.json()).data || []);
      } catch { /* fall back to core labels */ }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  const labelFor = useCallback((value) => {
    const row = types.find(t => t.value === value);
    return row?.label || FALLBACK_LABELS[value] || value.replace(/_/g, ' ');
  }, [types]);

  const kindOf = useCallback((value) => {
    const row = types.find(t => t.value === value);
    if (row) return row.parent_kind;
    if (value === 'robot' || value === 'krc') return 'top';
    return ['kpp', 'ksp', 'pc', 'smartpad'].includes(value) ? 'krc' : 'robot';
  }, [types]);

  return { types, labelFor, kindOf };
}

function statusChip(status) {
  if (status === 'fitted') return null;
  return <span className={`component-status component-status-${status}`}>{status.replace(/_/g, ' ')}</span>;
}

export function ComponentHistoryList({ componentId, authFetch, labelFor }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/components/${componentId}/history`);
        if (response.ok && !cancelled) setRows((await response.json()).data || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [componentId, authFetch]);

  if (rows === null) return <div className="component-history-empty">Loading history...</div>;
  if (!rows.length) return <div className="component-history-empty">No movements recorded.</div>;

  const typeLabel = (value) => (labelFor ? labelFor(value) : (FALLBACK_LABELS[value] || value || ''));

  const placeText = (assetRef, customer, parentType, parentSerial, status) => {
    if (assetRef || parentSerial) {
      const parent = parentSerial ? `${typeLabel(parentType)} ${parentSerial}` : null;
      return [customer, assetRef, parent].filter(Boolean).join(' · ');
    }
    if (status && status !== 'fitted') return status.replace(/_/g, ' ');
    return null;
  };

  return (
    <div className="component-history">
      {rows.map(row => {
        const from = placeText(row.from_asset_ref, row.from_customer_name, row.from_parent_type, row.from_parent_serial, row.from_status);
        const to = placeText(row.to_asset_ref, row.to_customer_name, row.to_parent_type, row.to_parent_serial, row.to_status);
        return (
          <div key={row.movement_id} className="component-history-row">
            <div className="component-history-when">
              {formatDateTime(row.moved_at)}
              {row.moved_by_name ? ` · ${row.moved_by_name}` : ''}
            </div>
            <div className="component-history-what">
              {from ? <>From <strong>{from}</strong> to <strong>{to || '—'}</strong></> : <>Registered at <strong>{to || '—'}</strong></>}
              {row.ticket_ref ? <span className="component-history-ticket"> · {row.ticket_ref}</span> : null}
            </div>
            {row.reason && <div className="component-history-reason">{row.reason}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function MoveModal({ component, kind, labelFor, authFetch, onClose, onMoved }) {
  const needsSlot = kind === 'top';
  const needsParent = kind === 'robot' || kind === 'krc';
  const [mode, setMode] = useState('fitted');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetResults, setAssetResults] = useState([]);
  const [destAsset, setDestAsset] = useState(null);
  const [destSlot, setDestSlot] = useState(1);
  const [destParents, setDestParents] = useState([]);
  const [destParentId, setDestParentId] = useState('');
  const [reason, setReason] = useState('');
  const [ticketRef, setTicketRef] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode !== 'fitted' || assetSearch.trim().length < 2) { setAssetResults([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await authFetch(`/api/v1/assets?search=${encodeURIComponent(assetSearch.trim())}&limit=8`);
        if (response.ok && !cancelled) {
          const payload = await response.json();
          setAssetResults(payload.data || []);
        }
      } catch { /* search is best-effort */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [assetSearch, mode, authFetch]);

  // Parts that fit a robot/KRC need a destination parent on the chosen asset.
  useEffect(() => {
    if (!needsParent || !destAsset) { setDestParents([]); setDestParentId(''); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/components?asset_id=${destAsset.asset_id}`);
        if (response.ok && !cancelled) {
          const parents = ((await response.json()).data || []).filter(row => row.component_type === kind);
          setDestParents(parents);
          setDestParentId(parents.length === 1 ? parents[0].component_id : '');
        }
      } catch { /* handled at submit */ }
    })();
    return () => { cancelled = true; };
  }, [destAsset, needsParent, kind, authFetch]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      let ticketId = null;
      if (ticketRef.trim()) {
        const ticketResponse = await authFetch(`/api/v1/tickets?search=${encodeURIComponent(ticketRef.trim())}&limit=5`);
        if (ticketResponse.ok) {
          const tickets = (await ticketResponse.json()).data || [];
          const match = tickets.find(t => t.ticket_ref?.toLowerCase() === ticketRef.trim().toLowerCase()) || tickets[0];
          if (match) ticketId = match.ticket_id;
        }
        if (!ticketId) {
          setError(`No ticket found for "${ticketRef.trim()}" — clear the field or use an exact reference.`);
          setSaving(false);
          return;
        }
      }
      let payload;
      if (mode !== 'fitted') {
        payload = { status: mode, reason, ticket_id: ticketId };
      } else if (needsParent) {
        payload = { parent_component_id: destParentId, status: 'fitted', reason, ticket_id: ticketId };
      } else {
        payload = { asset_id: destAsset?.asset_id, status: 'fitted', reason, ticket_id: ticketId };
        if (needsSlot) payload.slot = destSlot;
      }
      const response = await authFetch(`/api/v1/components/${component.component_id}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Move failed');
      onMoved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const destinationChosen = mode !== 'fitted' || (needsParent ? !!destParentId : !!destAsset);
  const fittedLabel = needsParent
    ? `Another ${kind === 'robot' ? 'robot' : 'KRC controller'}`
    : 'Another system';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content component-move-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Move {labelFor(component.component_type)} {component.serial_number}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-group">
            <label>Destination</label>
            <select value={mode} onChange={e => setMode(e.target.value)}>
              <option value="fitted">{fittedLabel}</option>
              {OFF_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {mode === 'fitted' && (
            <>
              <div className="form-group">
                <label>Destination system</label>
                {destAsset ? (
                  <div className="component-dest-chosen">
                    {destAsset.asset_ref} · {destAsset.customer_name || destAsset.business_name || ''}
                    <button type="button" className="btn-small" onClick={() => { setDestAsset(null); setAssetSearch(''); }}>Change</button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Search by asset ref, serial or customer..."
                      value={assetSearch}
                      onChange={e => setAssetSearch(e.target.value)}
                    />
                    {assetResults.length > 0 && (
                      <div className="component-dest-results">
                        {assetResults.map(a => (
                          <button key={a.asset_id} type="button" onClick={() => setDestAsset(a)}>
                            {a.asset_ref} · {String(a.robot_type || '').replace(/_/g, ' ')} · {a.customer_name || a.business_name || ''}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {needsSlot && destAsset && (
                <div className="form-group">
                  <label>Slot</label>
                  <select value={destSlot} onChange={e => setDestSlot(Number(e.target.value))}>
                    <option value={1}>1</option>
                    <option value={2}>2 (dual systems)</option>
                  </select>
                </div>
              )}
              {needsParent && destAsset && (
                <div className="form-group">
                  <label>Destination {kind === 'robot' ? 'robot' : 'KRC'}</label>
                  {destParents.length ? (
                    <select value={destParentId} onChange={e => setDestParentId(e.target.value)}>
                      <option value="">Select...</option>
                      {destParents.map(p => (
                        <option key={p.component_id} value={p.component_id}>
                          {labelFor(p.component_type)} {p.serial_number}{p.slot ? ` (slot ${p.slot})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="component-dest-empty">That system has no registered {kind === 'robot' ? 'robot' : 'KRC'} yet.</div>
                  )}
                </div>
              )}
            </>
          )}
          <div className="form-group">
            <label>Reason</label>
            <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is it moving? e.g. KPP fault swap" />
          </div>
          <div className="form-group">
            <label>Related ticket (optional)</label>
            <input type="text" value={ticketRef} onChange={e => setTicketRef(e.target.value)} placeholder="e.g. TCK-2026-0100" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={saving || !destinationChosen}>
            {saving ? 'Moving...' : 'Record move'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegisterModal({ assetId, components, types, labelFor, authFetch, onClose, onSaved }) {
  const activeTypes = types.filter(t => t.active);
  const [componentType, setComponentType] = useState(activeTypes.find(t => t.parent_kind !== 'top')?.value || 'kpp');
  const [serial, setSerial] = useState('');
  const [parentId, setParentId] = useState('');
  const [slot, setSlot] = useState(1);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const kind = types.find(t => t.value === componentType)?.parent_kind || 'krc';
  const needsParent = kind === 'robot' || kind === 'krc';
  const parents = components.filter(row => row.component_type === kind);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      let payload;
      if (kind === 'top') {
        payload = { component_type: componentType, serial_number: serial, asset_id: assetId, slot, status: 'fitted' };
      } else if (needsParent) {
        payload = { component_type: componentType, serial_number: serial, parent_component_id: parentId, status: 'fitted' };
      } else {
        payload = { component_type: componentType, serial_number: serial, asset_id: assetId, status: 'fitted' };
      }
      const response = await authFetch('/api/v1/components', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Failed to register component');
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content component-move-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Register component</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-group">
            <label>Part type</label>
            <select value={componentType} onChange={e => { setComponentType(e.target.value); setParentId(''); }}>
              {activeTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Serial number</label>
            <input type="text" value={serial} onChange={e => setSerial(e.target.value)} />
          </div>
          {kind === 'top' && (
            <div className="form-group">
              <label>Slot</label>
              <select value={slot} onChange={e => setSlot(Number(e.target.value))}>
                <option value={1}>1</option>
                <option value={2}>2 (dual systems)</option>
              </select>
            </div>
          )}
          {needsParent && (
            <div className="form-group">
              <label>Fitted inside</label>
              {parents.length ? (
                <select value={parentId} onChange={e => setParentId(e.target.value)}>
                  <option value="">Select...</option>
                  {parents.map(p => (
                    <option key={p.component_id} value={p.component_id}>
                      {labelFor(p.component_type)} {p.serial_number}{p.slot ? ` (slot ${p.slot})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="component-dest-empty">Register this system's {kind === 'robot' ? 'robot' : 'KRC'} first.</div>
              )}
            </div>
          )}
          {kind === 'asset' && (
            <p className="components-panel-hint">Fitted directly to this system.</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={saving || !serial.trim() || (needsParent && !parentId)}
          >
            {saving ? 'Saving...' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetComponentsPanel({ assetId }) {
  const { authFetch, user } = useAuth();
  const { types, labelFor, kindOf } = useComponentTypes();
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);

  const canMove = !READ_ONLY_ROLES.includes(user?.role);

  const fetchComponents = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/components?asset_id=${assetId}`);
      if (response.ok) setComponents((await response.json()).data || []);
    } catch (err) {
      console.error('Failed to fetch components:', err);
    } finally {
      setLoading(false);
    }
  }, [assetId, authFetch]);

  useEffect(() => { fetchComponents(); }, [fetchComponents]);

  if (loading) return <div className="loading">Loading components...</div>;

  const topLevel = components.filter(row => kindOf(row.component_type) === 'top');
  const systemParts = components.filter(row => kindOf(row.component_type) === 'asset');
  const childrenOf = (id) => components.filter(row => row.parent_component_id === id);
  const slots = [...new Set(topLevel.map(row => row.slot || 1))].sort();

  const renderRow = (component, isChild) => (
    <div key={component.component_id} className={`component-row${isChild ? ' component-row-child' : ''}`}>
      <span className="component-type">{labelFor(component.component_type)}</span>
      <span className="component-serial">{component.serial_number}</span>
      {statusChip(component.status)}
      <span className="component-actions">
        {canMove && (
          <button className="btn-small" onClick={() => setMoving(component)}>Move</button>
        )}
        <button
          className="btn-small"
          onClick={() => setHistoryFor(historyFor === component.component_id ? null : component.component_id)}
        >
          History
        </button>
      </span>
      {historyFor === component.component_id && (
        <div className="component-history-holder">
          <ComponentHistoryList componentId={component.component_id} authFetch={authFetch} labelFor={labelFor} />
        </div>
      )}
    </div>
  );

  return (
    <div className="components-panel">
      <div className="components-panel-header">
        <p className="components-panel-hint">
          Every part's current home and full movement history. Moving a robot or KRC takes its
          fitted parts with it; swap an individual part with its own Move action. Part categories
          are managed in Settings → Equipment.
        </p>
        {canMove && (
          <button className="btn-secondary btn-small" onClick={() => setRegistering(true)}>+ Register component</button>
        )}
      </div>

      {topLevel.length === 0 && systemParts.length === 0 && (
        <div className="components-empty">
          No components registered on this system yet. Robots and KRC controllers are created
          from the serial sheet; register parts here as their serials are recorded.
        </div>
      )}

      {slots.map(slot => (
        <div key={slot} className="component-slot">
          {slots.length > 1 && <h3 className="component-slot-title">SLOT {slot}</h3>}
          {topLevel.filter(row => (row.slot || 1) === slot).map(top => (
            <div key={top.component_id} className="component-group">
              {renderRow(top, false)}
              {childrenOf(top.component_id).map(child => renderRow(child, true))}
            </div>
          ))}
        </div>
      ))}

      {systemParts.length > 0 && (
        <div className="component-slot">
          <h3 className="component-slot-title">SYSTEM PARTS</h3>
          <div className="component-group">
            {systemParts.map(part => renderRow(part, false))}
          </div>
        </div>
      )}

      {moving && (
        <MoveModal
          component={moving}
          kind={kindOf(moving.component_type)}
          labelFor={labelFor}
          authFetch={authFetch}
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); fetchComponents(); }}
        />
      )}
      {registering && (
        <RegisterModal
          assetId={assetId}
          components={components}
          types={types}
          labelFor={labelFor}
          authFetch={authFetch}
          onClose={() => setRegistering(false)}
          onSaved={() => { setRegistering(false); fetchComponents(); }}
        />
      )}
    </div>
  );
}

export default AssetComponentsPanel;
